import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  IconButton,
  Chip,
  Card,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogContent,
  Divider,
  Tabs,
  styled,
  Tab,
  alpha,
  CardMedia,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Star as StarIcon,
  KeyboardBackspace,
  ContentCopy,
} from '@mui/icons-material';
import {
  IsNotNullOrEmpty,
  IsNullOrEmpty,
  isAuthTokenExpired,
} from '../../utils/validation';
import WidgetAPI from '../../services/api';
import { WidgetConfig } from '../../types';
import { getAnonymousToken, getAuthToken } from '../../utils/storage';
import useToggle from '../../hooks/useToggle';
import KeyFeatureDailog from '../KeyFeatureDailog';
import { getAssetURL } from '../../utils/url';
import { CarAvailability, InteractionType, LeadActionType } from '../../enum';
import { toast } from 'react-toastify';
import PreOnboardingDetails from './PreOnboardingDetails';
import PaymentInformationModel from './PaymentInformationModel';
import OnboardingForm from './OnboardingForm';
import OTPVerification from './OTPVerification';
import MilesTimeSlotPicker from '../MilesTimeSlotPicker';
import ViewSimilarCarsDialog from './ViewSimilarCarsDialog';
import { getUserTimezone } from '../../utils/timezone';
import Loader from '../Loader';
import DealershipContactDialog from './DealershipContactDialog';

export const BACKGROUND_GRADIENT =
  'linear-gradient(to bottom, #ffffff, #B1CAFF)';
export const BACKGROUND_GRADIENT_TRANSPARENT =
  'linear-gradient(to bottom, rgba(255, 255, 255, 0.5), rgba(177, 202, 255, 0.5))';
export const BUTTON_BG_COLOR = '#2a80f5';
export const BUTTON_BG_COLOR_GREEN = '#00BE62';


const INTERACTION_TYPE_BY_QUICK_OPTION: Partial<
  Record<NewLeadQuickOption, InteractionType>
> = {
  schedule_test_drive: InteractionType.testDrive,
  request_finance: InteractionType.financeApplication,
  price_and_payments: InteractionType.payments,
  history: InteractionType.vehicleHistory,
  share_vehicle: InteractionType.shareVehicle,
  value_trade: InteractionType.valueTrade,
  virtual_walkaround: InteractionType.virtualWalkaround,
  similar_vehicles: InteractionType.similarVehicles,
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`product-tabpanel-${index}`}
      aria-labelledby={`product-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const StyledTabs = styled(Tabs)(({ theme }) => ({
  minHeight: 48,
  '& .MuiTabs-indicator': {
    backgroundColor: '#1a1a1a',
    height: 3,
    borderRadius: '3px 3px 0 0',
  },
  '& .MuiTabs-flexContainer': {
    gap: theme.spacing(2),
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '15px',
  minHeight: 48,
  padding: '12px 20px',
  color: '#666',
  backgroundColor: '#f5f5f5',
  borderRadius: '8px 8px 0 0',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  minWidth: 100,

  '&:hover': {
    backgroundColor: '#ebebeb',
    color: '#333',
  },

  '&.Mui-selected': {
    color: '#1a1a1a',
    backgroundColor: '#fff',
    fontWeight: 700,
  },

  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    padding: '10px 16px',
    minWidth: 80,
  },
}));

// Types
type NewLeadDialogStep = 'information' | 'onboarding' | 'otp' | 'confirmation';

export type NewLeadQuickOption =
  | 'schedule_test_drive'
  | 'request_finance'
  | 'price_and_payments'
  | 'history'
  | 'save_vehicle'
  | 'share_vehicle'
  | 'live_agent'
  | 'value_trade'
  | 'virtual_walkaround'
  | 'similar_vehicles'
  | 'contact_dealer';

interface Vehicle {
  id: string;
  title: string;
  price: string;
  description: string;
  vinNumber: string;
  stockNumber: string;
  exterior: string;
  interior: string;
  bodyType: string;
  driveType: string;
  engine: string;
  modelCode: string;
  keyFeatures: string[];
  mileage: string;
  carType: string;
  link: string;
  deletedAt: null | string;
  dealerName: string;
  carBrandId: string;
  carModelId: string;
  carGenerationId: string;
  car_model?: any;
  car_generation?: any;
  priceBreakup?: {
    id: string | null;
    title: string;
    description: string;
    price: string;
    position: number;
  }[];
  priceBreakups?: {
    id: string | null;
    title: string;
    description: string;
    price: string;
    position: number;
  }[];
  photos: any[];
  userRole: string;
  createdAt: string;
  updatedAt: string;
  availability: string;
  dealerId?: string;
  dealer?: Dealer;
}

interface Dealer {
  id: string;
  firstName: string;
  lastName: string;
  dealerNames?: string[];
  dealerSettings: DealerSettings;
}

export interface DealerSettings {
  id: string;
  financeAppLink?: string;
  financeAppMessage?: string;
  googleLink?: string;
  financeSmsSendActive?: boolean;
  dealershipWebsite?: string;
  dealershipEmail?: string;
  dealershipPhone?: string;
  dealershipAddress?: string;
  dealershipHours?: string;
  dealershipSpecialOffers?: string;
  paperSizeId?: string;
  paperSize?: {
    id: string;
    name: string;
    width: number;
    height: number;
    description?: string;
  };
  promotionalOfferId?: string;
  promotionalOffer?: {
    id: string;
    name: string;
    code: string;
    description?: string;
  };
}

// interface FinancingOffer {
//   apr: number;
//   description: string;
//   disclaimer: string;
// }

interface CarDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  config: WidgetConfig;
  selectedCarData: any;
  updateLocalState(newState: any): void;
}

interface QuickOption {
  icon: string;
  title: string;
  shortTitle: string;
  code: NewLeadQuickOption;
  onboardingDialogDetails?: {
    title: string;
    description?: string;
    imagePath?: string;
    submitButtonText: string;
    footerText?: string;
  };
  infoDialogDetails?: {
    title: string;
    description: string;
    imagePath?: string;
    featureTitle?: string;
    featureDescription?: string;
    features: string[];
    submitButtonText: string;
    footerText?: string;
  };
}

const actionButtons: QuickOption[] = [
  {
    icon: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/ic_test_drive.png',
    title: 'Schedule Test Drive',
    shortTitle: 'Book Test Drive',
    code: 'schedule_test_drive',
    onboardingDialogDetails: {
      title: 'Request a Test Drive',
      description: 'Choose a time and we’ll have the vehicle ready.',
      submitButtonText: 'Request Test Drive',
      footerText: 'A sales associate will confirm your test drive shortly.',
      imagePath:
        'https://miles-cdn.qrbuddy.ai/static/autospotlight/img_request_test_drive.png',
    },
  },
  {
    icon: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/ic_payments.png',
    title: 'Price & Payments',
    shortTitle: 'Request Payment',
    code: 'price_and_payments',
    onboardingDialogDetails: {
      title: 'Get Real Payments',
      description:
        'We’ll connect you instantly with a sales associate for accurate monthly payments.',
      submitButtonText: 'Submit & Get Payments',
      footerText: 'No credit impact • Fast response • Secure',
    },
  },
  {
    icon: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/ic_value_your_trade.png',
    title: 'Value Your Trade',
    shortTitle: 'Value Your Trade',
    code: 'value_trade',
    onboardingDialogDetails: {
      title: 'Vehicle Trade-In',
      description: 'Get an estimated trade-in value.',
      submitButtonText: 'Request Vehicle Trade-In Info',
      footerText: 'A sales associate will be in touch with you shortly.',
      imagePath:
        'https://miles-cdn.qrbuddy.ai/static/autospotlight/img_vehicle_trade_in.png',
    },
    infoDialogDetails: {
      title: 'Vehicle Trade-In',
      description: 'Get an estimated trade-in value from the dealership',
      submitButtonText: 'Start Trade-In Request',
      footerText: 'Trade-in details are reviewed directly by the dealership.',
      imagePath:
        'https://miles-cdn.qrbuddy.ai/static/autospotlight/img_vehicle_trade_in.png',
      features: [
        'Dealer-managed trade-in review',
        'Fast & secure process',
        'No obligation',
        'Information sent directly to the dealership',
      ],
    },
  },
  {
    icon: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/ic_finance_app_new.png',
    title: 'Finance App',
    shortTitle: 'Finance\nApp',
    code: 'request_finance',
    onboardingDialogDetails: {
      title: 'Finance Application',
      imagePath:
        'https://miles-cdn.qrbuddy.ai/static/autospotlight/img_finance_application.png',
      submitButtonText: 'Continue',
    },
    infoDialogDetails: {
      title: 'Finance Application',
      description: 'Securely apply through the dealership.',
      imagePath:
        'https://miles-cdn.qrbuddy.ai/static/autospotlight/img_finance_application.png',
      submitButtonText: 'Open Finance Application',
      featureDescription:
        'Complete the dealership’s secure finance application to explore available financing options.',
      features: ['Fast & secure', 'No obligation', 'Dealer-managed'],
      footerText:
        'Finance applications are handled directly by the dealership.',
    },
  },
  {
    icon: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/ic_virtual_walkaround.png',
    title: 'Virtual Walkaround',
    shortTitle: 'Virtual Walkaround',
    code: 'virtual_walkaround',
    onboardingDialogDetails: {
      title: 'Virtual Walkaround Request',
      description:
        'We’ll connect you with a sales specialist for a live walkthrough of this vehicle.',
      imagePath:
        'https://miles-cdn.qrbuddy.ai/static/autospotlight/img_virtual_walkaround.png',
      submitButtonText: 'Submit Virtual Walkaround Request',
    },
    infoDialogDetails: {
      title: 'Virtual Walkaround',
      description:
        'Get a live, personalized walkthrough of this vehicle from a sales specialist.',
      imagePath:
        'https://miles-cdn.qrbuddy.ai/static/autospotlight/img_virtual_walkaround.png',
      submitButtonText: 'Finalize Virtual Walkthrough Request',
      featureTitle: 'What to Expect',
      features: [
        'Live video walkthrough of the exterior and interior',
        'Real-time answers to your questions',
        'No pressure, no obligation',
        'Available from anywhere',
      ],
    },
  },
  {
    icon: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/ic_vehicle_history.png',
    title: 'Vehicle History Report',
    shortTitle: 'Vehicle History',
    code: 'history',
    onboardingDialogDetails: {
      title: 'Vehicle History Report',
      description: 'Get a report on vehicle’s history.',
      imagePath:
        'https://miles-cdn.qrbuddy.ai/static/autospotlight/image_vehicle_history_report.png',
      submitButtonText: 'Request Vehicle History Report',
      footerText: 'A sales associate will be in touch with you shortly.',
    },
    infoDialogDetails: {
      title: 'Vehicle History Report',
      description: 'Request the official history report for this vehicle.',
      imagePath:
        'https://miles-cdn.qrbuddy.ai/static/autospotlight/image_vehicle_history_report.png',
      submitButtonText: 'Request Vehicle History Report',
      features: [
        'Accident & title history',
        'Ownership & service records',
        'Dealer-verified report',
      ],
      footerText: 'Your request will be securely sent to the dealership.',
    },
  },
  {
    icon: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/ic_similar_cars.png',
    title: 'Similar Vehicles',
    shortTitle: 'Similar Vehicles',
    code: 'similar_vehicles',
  },
  {
    icon: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/ic_contact_dealer.png',
    title: 'Contact Dealer',
    shortTitle: 'Contact Dealer',
    code: 'contact_dealer',
  },
  {
    icon: 'https://miles-cdn.qrbuddy.ai/static/ic_share_vehicle.png',
    title: 'share_vehicle',
    shortTitle: 'share',
    code: 'share_vehicle',
  },
];

const CarDetailsDialog: React.FC<CarDetailsDialogProps> = ({
  open,
  onClose,
  config,
  selectedCarData,
  updateLocalState,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [api] = useState(() => new WidgetAPI(config));

  const [localLoading, setLocalLoading] = useState(false);
  const [carId, setCarId] = useState<string | undefined>(
    // '822f9d00-e268-11f0-9838-09728599971c', // Local
    // 'a0d514e3-d185-11f0-8b4b-8b061a999436', // Prod
    selectedCarData?.id,
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [carData, setCarData] = useState<Vehicle | null>(null);
  const [dealerSettingsData, setDealerSettingsData] = useState<any>(null);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);
  const fetchingRef = useRef(false);

  const data = carData;

  const description = data?.description || '';
  // const dealerName = data?.dealer?.dealerNames?.[0] || data?.dealerName || '';
  const words = description?.trim() ? description?.split(/\s+/) : [];
  const isLong = words.length > 50;
  const displayedText = expanded ? description : words?.slice(0, 50)?.join(' ');
  const photoSource = [...(data?.photos || [])];
  // const photoItems =
  //   photoSource
  //     ?.map((photo: any) => getAssetURL(photo.key, photo?.url))
  //     ?.filter((url): url is string => Boolean(url)) || [];

  // const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // const financingOffer: FinancingOffer = {
  //   apr: 1.99,
  //   description: 'Financing Available',
  //   disclaimer: 'For qualified buyers • Limited time offer',
  // };

  const [tabValue, setTabValue] = useState(0);

  const [newLeadStep, setNewLeadStep] = useState<NewLeadDialogStep | undefined>(
    undefined,
  );
  const [newLeadOption, setNewLeadOption] = useState<QuickOption | undefined>(
    undefined,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [requestId, setRequestId] = useState<string>('');
  const [onboardingData, setOnboardingData] = useState<any>(undefined);
  const [timeLeft, setTimeLeft] = useState(60);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [downPaymentAmount, setDownPaymentAmount] = useState<number | null>(
    null,
  );
  const [isLeadActionLoading, setIsLeadActionLoading] =
    useState<boolean>(false);
  // const [financeResponse, setFinanceResponse] = useState<{
  //   url: string;
  //   message: string;
  // } | null>(null);

  const carModelLabel = 'Car Model';
  const carModelValue = data?.car_model?.title || null;
  const carGenerationLabel = 'Model Year';
  let carGenerationValue = null;

  if (data?.car_generation) {
    const title = data.car_generation.title;
    const year = data.car_generation.year;

    if (title && year) {
      carGenerationValue = `${title} - ${year}`;
    } else if (title) {
      carGenerationValue = title;
    } else if (year) {
      carGenerationValue = year;
    }
  }

  const carInfo = [
    ...(carModelValue ? [{ label: carModelLabel, value: carModelValue }] : []),
    ...(carGenerationValue
      ? [{ label: carGenerationLabel, value: carGenerationValue }]
      : []),
    { label: 'Exterior', value: data?.exterior },
    { label: 'Interior', value: data?.interior },
    { label: 'Body Type', value: data?.bodyType },
    { label: 'Drive Type', value: data?.driveType },
    { label: 'Engine', value: data?.engine },
    { label: 'Model Code', value: data?.modelCode },
    { label: 'Mileage', value: data?.mileage },
    { label: 'Car Type', value: data?.carType },
    { label: 'Dealer Name', value: data?.dealerName },
    { label: 'VIN Number', value: data?.vinNumber },
    { label: 'Stock Number', value: data?.stockNumber },
  ];

  const {
    toggle: keyFeaturesToggle,
    onOpen: keyFeaturesOnOpen,
    onClose: keyFeaturesOnClose,
  } = useToggle();

  const {
    toggle: newLeadCaptureToggle,
    onOpen: newLeadCaptureOnOpen,
    onClose: newLeadCaptureOnClose,
  } = useToggle();

  const {
    toggle: similarCarsToggle,
    onOpen: similarCarsOnOpen,
    onClose: similarCarsOnClose,
  } = useToggle();

  const {
    toggle: contactDealerToggle,
    onOpen: contactDealerOnOpen,
    onClose: contactDealerOnClose,
  } = useToggle();

  useEffect(() => {
    const fetchCar = async () => {
      // Prevent duplicate calls
      if (fetchingRef.current || !carId || carData) return;

      fetchingRef.current = true;
      setLocalLoading(true);
      try {
        const resultAction = await api.getCar(carId);

        if (resultAction.id) {
          setCarData(resultAction);
          setDealerSettingsData(resultAction?.dealer?.dealerSettings);

          // Make guest/ghost user entry
          if (!api.isAuthenticated()) {
            const authToken = getAuthToken();
            let anonymousAuthToken = getAnonymousToken();
            if (
              IsNullOrEmpty(authToken) &&
              (IsNullOrEmpty(anonymousAuthToken) ||
                isAuthTokenExpired(anonymousAuthToken))
            ) {
              await api.anonymousLogin();
            }

            let guestConversationId = api.getConversationId();

            if (!guestConversationId) {
              try {
                const result = await api.createGuestConversation();
                guestConversationId = result.conversationId;
                api.setGuestConversation(guestConversationId);

                updateLocalState({ conversationId: guestConversationId, isAnonymousAccess: true });
              } catch (error) {
                console.error('Failed to create Miles conversation:', error);
              }
            }

            api.createUserSession().then(() => {
              reportUserInteraction(InteractionType.cover, resultAction.id);
            });
          }

          api.handleLikeVehicle(resultAction.id);
        }
      } catch (err) {
        setError('Something went wrong');
      } finally {
        setLocalLoading(false);
        fetchingRef.current = false;
      }
    };

    fetchCar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carId, carData, api]);

  const handleSubmitOnboarding = async (leadData: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    timezone?: string;
    tcpaConsent: boolean;
  }) => {
    try {
      if (!leadData.email.trim()) {
        // Remove all non-numeric characters for storage and API
        const phoneDigits = leadData.phone.replace(/\D/g, '');

        // Auto-generate email from phone number
        leadData.email = `${phoneDigits}@qrbuddy.ai`;
      }

      const timezone = getUserTimezone();
      if (timezone && timezone !== 'UTC') {
        leadData.timezone = timezone;
      }

      const _data = {
        ...leadData,
        countryCode: '+1',
        dealerId: data?.dealerId || '',
        guestConversationId: localStorage.getItem('guestConversationId'),
      };
      setOnboardingData(_data);

      handleOtpSend(_data);
    } catch (error: any) {
      console.error('Failed to submit onboarding:', error);
      toast.error(error.message || 'Failed to submit. Please try again.');
    }
  };

  const handleVerifyOTP = async (otp: string) => {
    setIsLoading(true);

    try {
      const result = await api.verifyOTP(requestId, otp);

      if (result?.accessToken) {
        // Set authenticating flag to prevent loadConversationHistory from running
        // setIsAuthenticating(true);
        const newConversationId = result.conversationId || null;

        api.setAuth(
          result.accessToken,
          newConversationId || undefined,
          result.user,
        );

        // localStorage.setItem(config.AUTH_KEY, response?.accessToken);

        // if (window.MilesAIWidget?.destroy) {
        //   window.MilesAIWidget?.destroy();
        // }

        // if (window.MilesAIWidgetSetStorage) {
        //   window.MilesAIWidgetSetStorage({
        //     authToken: response?.accessToken,
        //     userData: response?.user,
        //     dealerId: data?.dealerId,
        //     isWelcome: false,
        //     isShowEnhanceView: false,
        //     isWebAppUser: true,
        //   });
        // }

        // if (window.MilesAIWidgetInit) {
        //   window.MilesAIWidgetInit({
        //     dealerId: data?.dealerId,
        //     dealerName: dealerName,
        //     isWelcome: false,
        //   });

        //   setTimeout(() => {
        //     if (window.MilesAIWidget?.setView) {
        //       window.MilesAIWidget?.setView('chat');
        //     }
        //   }, 1000);
        // }

        // scanCar();

        if (newLeadOption?.code === 'schedule_test_drive') {
          setNewLeadStep('confirmation');
        } else if (newLeadOption?.code === 'request_finance') {
          setNewLeadStep('confirmation');
          handleRequestActionClick(LeadActionType.requestFinance);
        } else if (newLeadOption?.code === 'price_and_payments') {
          handleGetPaymentsRequest();
          closeOnboardingForm();
        } else if (newLeadOption?.code === 'history') {
          handleRequestActionClick(LeadActionType.requestVehicleHistory);
          closeOnboardingForm();
        } else if (newLeadOption?.code === 'live_agent') {
          handleRequestActionClick(LeadActionType.requestLiveAgent);
          closeOnboardingForm();
        } else if (newLeadOption?.code === 'value_trade') {
          handleRequestActionClick(LeadActionType.requestVehicleTradeIn);
          closeOnboardingForm();
        } else if (newLeadOption?.code === 'virtual_walkaround') {
          handleRequestActionClick(LeadActionType.requestVirtualWalkaround);
          closeOnboardingForm();
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSend = async (data: any) => {
    try {
      setIsLoading(true);
      const result = await api.sendIdentity({ ...data });

      const expiryTimestamp = Date.now() + 60 * 1000;
      localStorage.setItem('otpExpiry', expiryTimestamp.toString());

      if (result.accessToken) {
      } else if (result.requestId) {
        setRequestId(result.requestId);
        setIsResendDisabled(true);
        setNewLeadStep('otp');
        setTimeLeft(60);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = () => {
    if (isResendDisabled) return;
    setIsResendDisabled(true);
    setTimeLeft(60);
    handleOtpSend(onboardingData);
  };

  const handleQuickOptionClicked = (option?: QuickOption, downPayment = -1) => {
    setNewLeadOption(option);

    switch (option?.code) {
      case 'schedule_test_drive':
      case 'value_trade':
      case 'virtual_walkaround':
      case 'price_and_payments':
      case 'history':
      case 'live_agent':
        // case 'request_finance':
        if (api.isAuthenticated()) {
          if (
            newLeadStep !== 'information' &&
            [
              'value_trade',
              'virtual_walkaround',
              'history',
              'price_and_payments',
            ].includes(option?.code)
          ) {
            reportUserInteraction(getInteractionTypeFromQuickOption(option.code))
            setNewLeadStep('information');
            newLeadCaptureOnOpen();
            return;
          } else if (
            [
              'value_trade',
              'virtual_walkaround',
              'history',
              'price_and_payments',
            ].includes(option?.code)
          ) {
            closeOnboardingForm();
          }

          if (option?.code === 'schedule_test_drive') {
            setNewLeadStep('confirmation');
            newLeadCaptureOnOpen();
            // reportUserInteraction(getInteractionTypeFromQuickOption(option.code))
            // } else if (option?.code === 'request_finance') {
            //   if (newLeadStep !== 'confirmation') {
            //     handleRequestActionClick(LeadActionType.requestFinance);
            //     setNewLeadStep('confirmation');
            //     newLeadCaptureOnOpen();
            //   } else {
            //     window.open(financeResponse?.url, '_blank');
            //     closeOnboardingForm();
            //   }
          } else if (option?.code === 'price_and_payments') {
            handleGetPaymentsRequest(downPayment);
          } else if (option?.code === 'history') {
            handleRequestActionClick(LeadActionType.requestVehicleHistory);
          } else if (option?.code === 'value_trade') {
            handleRequestActionClick(LeadActionType.requestVehicleTradeIn);
          } else if (option?.code === 'virtual_walkaround') {
            handleRequestActionClick(LeadActionType.requestVirtualWalkaround);
          } else {
            handleRequestActionClick(LeadActionType.requestLiveAgent);
          }
        } else {
          if (
            newLeadStep !== 'information' &&
            [
              'value_trade',
              'virtual_walkaround',
              'history',
              'price_and_payments',
            ].includes(option?.code)
          ) {
            reportUserInteraction(getInteractionTypeFromQuickOption(option.code))
            setNewLeadStep('information');
          } else {
            reportUserInteraction(getInteractionTypeFromQuickOption(option.code))
            setNewLeadStep('onboarding');
          }
          newLeadCaptureOnOpen();
        }
        break;
      case 'request_finance':
        if (api.isAuthenticated()) {
          handleRequestActionClick(LeadActionType.requestFinance);
        }
        if (newLeadStep !== 'confirmation') {
          reportUserInteraction(getInteractionTypeFromQuickOption(option.code))
          setNewLeadStep('confirmation');
          newLeadCaptureOnOpen();
        } else {
          gotoDealershipFinanceAppPage();
          closeOnboardingForm();
        }
        break;
      case 'share_vehicle':
        reportUserInteraction(getInteractionTypeFromQuickOption(option.code))
        newLeadCaptureOnOpen();
        break;
    }
  };

  const handleRequestActionClick = async (actionType: LeadActionType) => {
    try {
      setIsLeadActionLoading(true);
      const response = await api.requestLeadAction({
        carId: data?.id ?? '',
        actionType: actionType,
        scheduleDate: null,
      });

      if (response) {
        if (actionType === LeadActionType.requestFinance) {
          // setFinanceResponse({
          //   url: response?.url,
          //   message: response?.message,
          // });
        } else {
          toast.success(
            'Information submitted successfully. A sales rep will be in touch shortly.',
          );
        }
      }
    } catch (error) {
      console.error('Error occurred while requesting action:', error);
    } finally {
      setIsLeadActionLoading(false);
    }
  };

  const handleGetPaymentsRequest = async (downPayment = -1) => {
    try {
      const payload = {
        carId: data?.id ?? '',
        actionType: LeadActionType.requestGetPayments,
        downPaymentAmount: downPayment >= 0 ? downPayment : downPaymentAmount,
      };

      closeOnboardingForm();

      const result = await api.requestLeadAction(payload);

      if (result) {
        toast.success(
          'Information submitted successfully. A sales rep will be in touch shortly.',
        );
      }
    } catch (e) {
      console.error('Error occurred while requesting get payments:', error);
    }
  };

  const getInteractionTypeFromQuickOption = (
    option?: NewLeadQuickOption,
  ): InteractionType | undefined => {
    if (!option) return undefined;
    return INTERACTION_TYPE_BY_QUICK_OPTION[option];
  };

  const reportUserInteraction = (interactionType?: InteractionType, carId?: string) => {
    if (interactionType) {
      api.createUserInteraction({
        actionType: interactionType.toString(),
        carId: carId ?? data?.id ?? ''
      });
    }
  }

  const closeOnboardingForm = () => {
    newLeadCaptureOnClose();
    setOnboardingData(undefined);
    setNewLeadStep(undefined);
    setNewLeadOption(undefined);
  };

  // const handlePrevImage = () => {
  //   if (carData?.photos) {
  //     setCurrentImageIndex((prev) =>
  //       prev === 0 ? carData.photos.length - 1 : prev - 1,
  //     );
  //   }
  // };

  // const handleNextImage = () => {
  //   if (carData?.photos) {
  //     setCurrentImageIndex((prev) =>
  //       prev === carData.photos.length - 1 ? 0 : prev + 1,
  //     );
  //   }
  // };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    switch (newValue) {
      case 0:
        reportUserInteraction(InteractionType.description);
        break;
      case 1:
        reportUserInteraction(InteractionType.features);
        break;
      case 2:
        reportUserInteraction(InteractionType.specs);
        break;
    }
    setTabValue(newValue);
  };

  const gotoDealershipWebsite = () => {
    if (IsNotNullOrEmpty(dealerSettingsData?.dealershipWebsite)) {
      window.open(dealerSettingsData?.dealershipWebsite, '_blank');
    }
  };

  const gotoDealershipFinanceAppPage = () => {
    if (IsNotNullOrEmpty(dealerSettingsData?.financeAppLink)) {
      window.open(dealerSettingsData?.financeAppLink, '_blank');
    } else {
      toast.error('The finance application link has not been added by the dealer.');
    }
  };

  // const gotoDealershipGoogleReviewPage = () => {
  //   if (IsNotNullOrEmpty(dealerSettingsData?.googleLink)) {
  //     window.open(dealerSettingsData?.googleLink, '_blank');
  //   }
  // };

  const handleOpenLocation = () => {
    const url = `https://www.google.com/maps/search/?api=1&query=${getEncodeFullAddress()}`;
    window.open(url, '_blank');
  };

  const getEncodeFullAddress = (): string => {
    return encodeURIComponent(
      `${data?.dealerName} ${dealerSettingsData?.dealershipAddress}`,
    );
  };

  const handleCopyLink = (shareLink?: string) => {
    if (!shareLink) {
      toast.error('No link to copy.');
      return;
    }

    if (!navigator.clipboard?.writeText) {
      toast.error('Copy not supported in this browser.');
      return;
    }

    navigator.clipboard
      .writeText(shareLink)
      .then(() => toast.success('Copied to clipboard'))
      .catch(() => toast.error('Failed to copy link.'));
  };

  const scrollLeft = () => {
    let newIndex = currentIndex - 1;
    if (newIndex < 0) {
      newIndex = newIndex = photoSource?.length - 1;
    }
    setCurrentIndex(newIndex);
    scrollToIndex(newIndex);
    reportUserInteraction(InteractionType.photos);
  };

  const scrollRight = () => {
    let newIndex = currentIndex + 1;
    if (newIndex >= photoSource?.length) {
      newIndex = 0;
    }
    setCurrentIndex(newIndex);
    scrollToIndex(newIndex);
    reportUserInteraction(InteractionType.photos);
  };

  const scrollToIndex = (index: number) => {
    const el = scrollRef?.current;
    if (!el) return;

    const childWidth =
      el?.firstChild instanceof HTMLElement
        ? el?.firstChild?.offsetWidth + 16
        : 300;

    el?.scrollTo({ left: index * childWidth, behavior: 'smooth' });
  };

  useEffect(() => {
    const savedExpiry = localStorage.getItem('otpExpiry');
    const now = Date.now();

    if (!savedExpiry) return;

    if (parseInt(savedExpiry) > now) {
      const remaining = Math.floor((parseInt(savedExpiry) - now) / 1000);
      setTimeLeft(remaining);
      setIsResendDisabled(true);
    } else {
      setTimeLeft(0);
      setIsResendDisabled(false);
    }
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setIsResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(timer);
  }, [timeLeft]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={isMobile}
        maxWidth='xs'
        fullWidth
        sx={{
          '& .MuiDialog-container': {
            alignItems: isMobile ? 'stretch' : 'flex-end',
            justifyContent: isMobile ? 'center' : 'flex-end',
          },
        }}
        PaperProps={{
          sx: {
            bgcolor: '#E8EBF4',
            backgroundImage: 'none',
            maxHeight: isMobile ? '100vh' : '80vh',
            m: isMobile ? 0 : 2,
            mb: isMobile ? 0 : '85px',
          },
        }}
      >
        {localLoading ? (
          <Loader />
        ) : (
          <>
            <Box
              sx={{
                borderRadius: '12px',
                mx: 1.5,
                pb: 1,
              }}
            >
              {/* Profile Section */}
              <Box
                sx={{
                  px: 2.5,
                  py: 1.5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  // backgroundImage: 'url(/static/img_appbar_banner_mobile.png)',
                  // backgroundRepeat: 'no-repeat',
                  // backgroundSize: 'cover',
                  // backgroundPosition: 'right',
                }}
              >
                <IconButton
                  onClick={() => onClose()}
                  sx={{
                    left: 0,
                    py: 0.5,
                    // position: 'absolute',
                  }}
                  aria-label='Go back'
                >
                  <KeyboardBackspace
                    fontSize='large'
                    sx={{ color: 'primary' }}
                  />
                </IconButton>

                <Box
                  sx={{
                    // display: 'flex',
                    // alignItems: 'center',
                    // justifyContent: 'center',
                    // width: '100%',
                    position: 'relative',
                    bgcolor: '#555555',
                    p: 1,
                    borderRadius: 2,
                    cursor: 'pointer',
                  }}
                  onClick={() => onClose()}
                >
                  <Typography variant='body1' sx={{ color: 'white' }}>
                    Change Vehicle
                  </Typography>
                </Box>

                <Box />

                {/* {
                  <Button
                    variant='contained'
                    sx={{
                      background: '#fff',
                      color: 'text.primary',
                      borderRadius: '12px',
                      border: '1px solid #44b0d5',
                      fontWeight: 400,
                      // position: 'absolute',
                      // right: 0,
                      // py: 0.5,
                      boxShadow: 1,
                    }}
                    onClick={gotoLoginPage}
                  >
                    Log In
                  </Button>
                } */}
              </Box>

              {/* Images Section */}
              <Box
                sx={{
                  // mt:
                  //   loggedInUserId &&
                  //     userRole === RoleType.user &&
                  //     data?.dealerId
                  //     ? 2
                  //     : 3,
                  // display: { xs: 'block', sm: 'none' },
                  position: 'relative',
                  width: '100%',
                  overflow: 'hidden',
                  justifyContent: 'center',
                  textAlign: 'center',
                }}
              >

                {photoSource?.length > 0 ? (
                  <Box
                    key={currentIndex}
                    sx={{
                      // position: 'relative',
                      opacity:
                        data?.availability !== CarAvailability.available
                          ? 0.5
                          : 1,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      height: 220,
                      transition: 'transform 0.5s ease',
                    }}
                  >
                    {/* Image */}
                    <Box
                      key={currentIndex}
                      sx={{
                        opacity:
                          data?.availability !== CarAvailability.available
                            ? 0.5
                            : 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        height: 220,
                        transition: 'transform 0.5s ease',
                      }}
                    >
                      <Box
                        component='img'
                        src={getAssetURL(
                          config.assetsUrl,
                          photoSource[currentIndex]?.key,
                          photoSource[currentIndex]?.url,
                        )}
                        alt={`Car ${currentIndex + 1}`}
                        sx={{
                          width: '100%', // '85%', // centered image width
                          // height: '200px',
                          objectFit: 'contain',
                        }}
                        // onClick={() => openFullscreen(currentIndex)}
                        onClick={() => reportUserInteraction(InteractionType.photos)}
                      />
                    </Box>

                    {/* Left Button */}
                    {photoSource?.length > 1 && (
                      <>
                        <IconButton
                          onClick={scrollLeft}
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: 10,
                            transform: 'translateY(-50%)',
                            zIndex: 10,
                            backgroundColor: 'rgba(255, 255, 255, 0.486)',
                            boxShadow: 2,
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,1)',
                            },
                          }}
                        >
                          <ChevronLeft />
                        </IconButton>

                        {/* Right Button */}
                        <IconButton
                          onClick={scrollRight}
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            right: 10,
                            transform: 'translateY(-50%)',
                            zIndex: 10,
                            backgroundColor: 'rgba(255,255,255,0.486)',
                            boxShadow: 2,
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,1)',
                            },
                          }}
                        >
                          <ChevronRight />
                        </IconButton>
                      </>
                    )}
                    {/* Sold Out Overlay */}
                    {data?.availability !== CarAvailability.available && (
                      <Box
                        component='img'
                        src='https://miles-cdn.qrbuddy.ai/static/sold_out.png'
                        alt='Sold Out'
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          maxWidth: '80%',
                          maxHeight: '80%',
                          pointerEvents: 'none',
                          userSelect: 'none',
                        }}
                      />
                    )}
                  </Box>
                ) : (
                  <Box
                    key={currentIndex}
                    sx={{
                      // position: 'relative',
                      opacity:
                        data?.availability !== CarAvailability.available
                          ? 0.5
                          : 1,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      height: 220,
                      transition: 'transform 0.5s ease',
                    }}
                  >
                    {/* Image */}
                    <Box
                      key={currentIndex}
                      sx={{
                        opacity:
                          data?.availability !== CarAvailability.available
                            ? 0.5
                            : 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        height: 220,
                        transition: 'transform 0.5s ease',
                      }}
                    >
                      <Box
                        component='img'
                        src={`${config?.webUrl}/static/car_placeholder.png`}
                        alt={`Car ${currentIndex + 1}`}
                        sx={{
                          width: '100%', // '85%', // centered image width
                          // height: '200px',
                          objectFit: 'contain',
                        }}
                        // onClick={() => openFullscreen(currentIndex)}
                        onClick={() => reportUserInteraction(InteractionType.photos)}
                      />
                    </Box>

                    {/* Sold Out Overlay */}
                    {data?.availability !== CarAvailability.available && (
                      <Box
                        component='img'
                        src='https://miles-cdn.qrbuddy.ai/static/sold_out.png'
                        alt='Sold Out'
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          maxWidth: '80%',
                          maxHeight: '80%',
                          pointerEvents: 'none',
                          userSelect: 'none',
                        }}
                      />
                    )}
                  </Box>
                )}
                {/* In Stock overlay */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: 5,
                    top: 5,
                    bgcolor: '#00bf63',
                    borderRadius: 4,
                    px: 1,
                    py: 0.3,
                  }}
                >
                  <Typography
                    variant='body2'
                    sx={{
                      color: 'white',
                    }}
                  >
                    In Stock
                  </Typography>
                </Box>

                {/* Request Virtual Walkaround overlay */}
                {/* {!loggedInUserId && (
                          <Box
                            sx={{
                              position: 'absolute',
                              right: 5,
                              top: 5,
                              // bgcolor: 'white',
                              background:
                                'linear-gradient(to bottom, #5E94EA, #3C77DD)',
                              borderRadius: 4,
                              border: '1px solid #208af5',
                              px: 1,
                              py: 0.3,
                              // boxShadow: 1,
                              display: 'flex',
                              flexDirection: 'row',
                            }}
                            onClick={() => {
                              handleQuickOptionClicked(
                                actionButtons.find(
                                  (item) => item.code === 'virtual_walkaround',
                                ),
                              );
                            }}
                          >
                            <Box
                              component={'img'}
                              src='/static/icon/ic_noun_camera_video.svg'
                              sx={{ height: '25px', mr: 1 }}
                            />
                            <Typography
                              variant='body2'
                              sx={{
                                color: 'white',
                              }}
                            >
                              {t('button.request_virtual_walkaround')}
                            </Typography>
                          </Box>
                        )} */}

                {/* Live agent overlay */}
                {/* {!loggedInUserId && (
                        <Box
                          sx={{
                            position: 'absolute',
                            right: 10,
                            top: 5,
                            bgcolor: 'white',
                            borderRadius: 4,
                            px: 2,
                            py: 0.5,
                            boxShadow: 2,
                            display: 'flex',
                            flexDirection: 'row',
                          }}
                          onClick={() => {
                            handleQuickOptionClicked(
                              quickOptions.find(
                                (item) => item.code === 'live_agent',
                              ),
                            );
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: -8,
                              transform: 'translateY(-50%)',
                              zIndex: 10,
                              height: 15,
                              width: 15,
                              bgcolor: '#00bf63',
                              borderRadius: '50%',
                            }}
                          />

                          <Typography
                            sx={{
                              color: 'GrayText',
                              '& span': {
                                color: theme.palette.primary.main,
                                fontWeight: 500,
                              },
                            }}
                          >
                            <HtmlRenderer htmlString={t('live_agent')} />
                          </Typography>
                          <Box
                            component={'img'}
                            src='/static/ic_live_agent.png'
                            sx={{ height: '25px', ml: 1 }}
                          />
                        </Box>
                      )} */}
                {/* Share and Favorite overlay */}
                {/* {!loggedInUserId && (
              <Box
                sx={{
                  position: 'absolute',
                  right: 5,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 1,
                }}
              >                
                <Box
                  // component={'img'}
                  // src='/static/ic_favorite.png'
                  // sx={{ height: '30px' }}
                  onClick={gotoLoginPage}
                >
                  <FavoriteBorderOutlinedIcon />
                </Box>
              </Box>
            )} */}
              </Box>
              {/* Overlapping Profile Card */}

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '100%',
                  p: 1,
                  my: 1,
                }}
              >
                <Grid
                  container
                  spacing={1}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Grid size={7} sx={{ gap: 0.2 }}>
                    <Typography fontSize='14px' fontWeight={500}>
                      {data?.title}
                    </Typography>
                    {/* <Typography
                            sx={{
                              fontSize: '18px',
                              fontWeight: 600,
                              background: (theme: any) =>
                                theme.palette.gradients.primary,
                              backgroundClip: 'text',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              color: 'transparent',
                            }}
                          >
                            {parsedPrice?.amount !== '-'
                              ? `${parsedPrice?.symbolOrCode}${parsedPrice?.amount}`
                              : '-'}
                          </Typography> */}
                    <Typography
                      fontSize='12px'
                      fontWeight={400}
                      color={'text.secondary'}
                    >
                      {data?.mileage ? `${data?.mileage ?? 0} Miles` : ''}{' '}
                      {' - '}
                      {data?.engine ? `${data?.engine ?? 0}` : ''}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 0.5,
                        alignItems: 'center',
                      }}
                    // onClick={gotoDealershipGoogleReviewPage}
                    >
                      <StarIcon sx={{ color: 'gold' }} />
                      <Typography fontWeight={600} fontSize='14px'>
                        (4.7)
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid
                    size={5}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                    }}
                  >
                    <Button
                      variant='contained'
                      fullWidth
                      sx={{
                        background: BUTTON_BG_COLOR_GREEN,
                        fontSize: { xs: '0.768rem', sm: '1rem' },
                        lineHeight: 1,
                        fontWeight: 500,
                        px: 1,
                        textTransform: 'none',
                      }}
                      onClick={() => {
                        handleQuickOptionClicked(
                          actionButtons.find(
                            (item) => item.code === 'price_and_payments',
                          ),
                        );
                      }}
                    >
                      Get Today’s Price
                    </Button>
                    <Button
                      variant='contained'
                      fullWidth
                      sx={{
                        background: BUTTON_BG_COLOR,
                        fontSize: { xs: '0.768rem', sm: '1rem' },
                        lineHeight: 1,
                        fontWeight: 500,
                        px: 1,
                        textTransform: 'none',
                      }}
                      onClick={() => {
                        handleQuickOptionClicked(
                          actionButtons.find(
                            (item) => item.code === 'share_vehicle',
                          ),
                        );
                      }}
                    >
                      Share This Vehicle
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              <Box
                sx={{
                  backgroundColor: '#fff',
                  borderRadius: '16px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                  overflow: 'hidden',
                  // transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 15px 50px rgba(0,0,0,0.12)',
                  },
                }}
              >
                {/* Tabs Section */}
                <Box
                  sx={{

                    // px: { xs: 2, sm: 3, md: 4 },
                    // pt: { xs: 2, sm: 3 },
                    px: 2,
                    py: 2,
                  }}
                >
                  <StyledTabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label='product details tabs'
                    variant={isMobile ? 'fullWidth' : 'standard'}
                  >
                    <StyledTab label={'Description'} />
                    <StyledTab label={'Features'} />
                    <StyledTab label={'Specs'} />
                  </StyledTabs>
                </Box>

                {/* Content Section */}
                <Box
                  sx={{
                    px: { xs: 2, sm: 3, md: 4 },
                    pb: { xs: 3, sm: 4, md: 5 },
                  }}
                >
                  <TabPanel value={tabValue} index={0}>
                    <Typography
                      variant='body1'
                      sx={{
                        lineHeight: 1.8,
                        color: '#333',
                        fontSize: {
                          xs: '14px',
                          sm: '15px',
                          md: '16px',
                        },
                        textAlign: 'justify',
                        fontFamily: '"Segoe UI", "Roboto", sans-serif',
                        letterSpacing: '0.2px',
                      }}
                    >
                      {displayedText}
                      {isLong && (
                        <Box
                          component='span'
                          onClick={() => {
                            if (!expanded) {
                              reportUserInteraction(InteractionType.description);
                            }
                            setExpanded(!expanded)
                          }
                          }
                          sx={{
                            cursor: 'pointer',
                            verticalAlign: 'middle',
                            ml: 1,
                            display: 'inline-flex',
                            alignItems: 'center',
                            // color: 'primary.light',
                            color: 'black',
                            fontWeight: 600,
                            userSelect: 'none',
                          }}
                        >
                          {expanded ? `Less` : `More`}
                        </Box>
                      )}
                    </Typography>
                  </TabPanel>

                  <TabPanel value={tabValue} index={1}>
                    {Array.from({
                      length: Math.ceil(carInfo.length / 2),
                    }).map((_, i) => {
                      const pair = carInfo.slice(i * 2, i * 2 + 2);
                      return (
                        <Box key={i} mb={2}>
                          <Grid container spacing={2}>
                            {pair.map(({ label, value }) => (
                              <Grid size={6} key={label}>
                                <Typography
                                  sx={{
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: 'text.secondary',
                                  }}
                                >
                                  {label}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: '14px',
                                    color: '#000',
                                    display: 'flex',
                                    alignItems: 'center',
                                    wordBreak: 'break-all',
                                  }}
                                >
                                  {label === 'Car Type' &&
                                    (value?.toString().toLowerCase() === 'used' ||
                                      value?.toString().toLowerCase() ===
                                      'new') ? (
                                    <Chip
                                      label={
                                        value?.toString().toLowerCase() ===
                                          'used'
                                          ? 'Used'
                                          : 'New'
                                      }
                                      size='medium'
                                      sx={{
                                        mt: 0.5,
                                        backgroundColor:
                                          value === 'Used'
                                            ? '#FFF5E7'
                                            : '#DFFFE3',
                                        color:
                                          value === 'Used'
                                            ? '#E08C0E'
                                            : '#2E7D32',
                                        fontWeight: 600,
                                        fontSize: 14,
                                        height: 25,
                                      }}
                                    />
                                  ) : value ? (
                                    value
                                  ) : (
                                    <Box component='span' sx={{ ml: 1 }}>
                                      -
                                    </Box>
                                  )}
                                </Typography>
                              </Grid>
                            ))}
                          </Grid>

                          {/* Show divider only if the next pair has content */}
                          {(() => {
                            const nextPair = carInfo.slice(
                              (i + 1) * 2,
                              (i + 1) * 2 + 2,
                            );
                            const hasNextContent = nextPair.some(
                              ({ value }) => value,
                            );
                            return hasNextContent ? (
                              <Divider sx={{ mt: 2 }} />
                            ) : null;
                          })()}
                        </Box>
                      );
                    })}
                  </TabPanel>

                  <TabPanel value={tabValue} index={2}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        mt: 1,
                      }}
                    >
                      {data?.keyFeatures
                        ?.slice(0, 10)
                        ?.map((feature: string, index: number) => (
                          <Chip
                            key={index}
                            icon={
                              <Box
                                component={'img'}
                                src={
                                  'https://miles-cdn.qrbuddy.ai/static/autospotlight/ic_motion_photos_on.svg'
                                }
                                sx={{
                                  height: 16,
                                }}
                              />
                            }
                            label={feature}
                            size='medium'
                            sx={{
                              padding: '12px 2px',
                              backgroundColor: '#007CB41F',
                              color: '#93989F',
                              fontWeight: 400,
                              fontSize: '14px',
                            }}
                          />
                        ))}
                    </Box>
                    {(data?.keyFeatures?.length ?? 0) > 10 && (
                      <Box mt={1}>
                        <Typography
                          onClick={keyFeaturesOnOpen}
                          sx={{
                            fontSize: '14px',
                            fontWeight: 500,
                            color: 'info.light',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                          }}
                        >
                          {'All'} {data?.keyFeatures?.length} {'Highlights'}
                        </Typography>
                      </Box>
                    )}
                  </TabPanel>
                </Box>
              </Box>

              {data?.dealerId && (
                <Box
                  sx={{
                    mt: { xs: 0, sm: 2 },
                    mb: { xs: 0, sm: 2 },
                    // borderRadius: 2,
                    // background: `url('/static/img_miles_widget_bg.png')`,
                    // backgroundPosition: 'bottom',
                    // backgroundSize: 'cover',
                    // backgroundRepeat: 'no-repeat',
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    sx={{
                      p: 1,
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'repeat(4, minmax(0, 1fr))',
                        sm: 'repeat(4, minmax(0, 1fr))',
                      },
                      gap: 1,
                      width: '100%',
                      maxWidth: 720,
                    }}
                  >
                    {actionButtons
                      .filter((item) =>
                        [
                          'schedule_test_drive',
                          'price_and_payments',
                          'value_trade',
                          'request_finance',
                          'virtual_walkaround',
                          'history',
                          'similar_vehicles',
                          'contact_dealer',
                        ].includes(item.code),
                      )
                      .map((item) => {
                        return (
                          <Card
                            sx={{
                              borderRadius: 2,
                              // boxShadow: 4,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              p: 1,
                              height: '100%',
                              gap: 1,
                              textAlign: 'center',
                            }}
                            onClick={() => {
                              if (item.code === 'similar_vehicles') {
                                similarCarsOnOpen();
                                reportUserInteraction(InteractionType.similarVehicles);
                              } if (item.code === 'contact_dealer') {
                                contactDealerOnOpen();
                                reportUserInteraction(InteractionType.contactDealer);
                              } else {
                                handleQuickOptionClicked(item);
                              }
                            }}
                          >
                            <Box
                              sx={{
                                height: '40px',
                                width: '40px',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <Box
                                component={'img'}
                                src={item.icon}
                                alt={(item.title)}
                                sx={{
                                  width: '40px',
                                  height: '40px',
                                  objectFit: 'contain',
                                  textAlign: 'center',
                                }}
                              />
                            </Box>
                            <Typography
                              sx={{
                                fontSize: {
                                  xs: '0.7rem',
                                  // sm: '1rem',
                                },
                                lineHeight: 1.2,
                                textTransform: 'capitalize',
                                fontWeight: 400,
                                whiteSpace: 'pre-line',
                              }}
                            >
                              {item.shortTitle}
                            </Typography>
                          </Card>
                        );
                      })}
                  </Box>
                </Box>
              )}
              {/* Mobile View */}

              {/* {isMobile && renderDealerWebsiteAndLocation()} */}
            </Box>
          </>
        )}
      </Dialog>

      <KeyFeatureDailog
        alertTemplateToggle={keyFeaturesToggle}
        alertTemplateOnClose={keyFeaturesOnClose}
        features={data?.keyFeatures || []}
      />

      {newLeadCaptureToggle && newLeadOption && (
        <Dialog
          open={newLeadCaptureToggle}
          onClose={() => closeOnboardingForm()}
          fullWidth
          maxWidth='sm'
          PaperProps={{
            sx: {
              borderRadius: { xs: 2, sm: 3 },
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              overflow: 'visible', // Changed from 'hidden' to allow image to overflow
              position: 'relative',
              mt: 8, // Add margin top to make room for the image
              // width: "calc(100% - 10px)"
            },
          }}
        >
          {/* Rounded image anchored at top center */}
          {/* <Box
            sx={{
              position: 'absolute',
              top: -30,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1,
              width: 60,
              height: 60,
              borderRadius: '50%',
              overflow: 'hidden', // This ensures the image is clipped to circle
              border: '4px solid white',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              backgroundColor: 'white', // Fallback background
              p: 1,
            }}
          >
            <Box
              component='img'
              src={
                quickOptions.find((item) => item.code === newLeadOption.code)
                  ?.icon || 'https://miles-cdn.qrbuddy.ai/static/qr_buddy_logo_blue_small.png'
              }
              alt='Dialog header'
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover', // Ensures image covers the circle without distortion
                objectPosition: 'center', // Centers the image
              }}
            />
          </Box> */}

          <DialogContent
            sx={{
              // p: { xs: 2, sm: 3 }
              p: 0,
              // pt: 2,
            }}
          >
            {['value_trade', 'virtual_walkaround', 'history'].includes(
              newLeadOption.code,
            ) &&
              newLeadStep === 'information' && (
                <PreOnboardingDetails
                  onContinue={() => handleQuickOptionClicked(newLeadOption)}
                  onBack={() => {
                    closeOnboardingForm();
                  }}
                  newLeadOption={newLeadOption.code}
                  title={newLeadOption.infoDialogDetails?.title}
                  description={newLeadOption.infoDialogDetails?.description}
                  featureTitle={newLeadOption.infoDialogDetails?.featureTitle}
                  featureDescription={
                    newLeadOption.infoDialogDetails?.featureDescription
                  }
                  features={newLeadOption.infoDialogDetails?.features ?? []}
                  submitButtonText={
                    newLeadOption.infoDialogDetails?.submitButtonText
                  }
                  footerText={newLeadOption.infoDialogDetails?.footerText}
                  imagePath={newLeadOption.infoDialogDetails?.imagePath}
                />
              )}

            {newLeadOption.code === 'price_and_payments' &&
              newLeadStep === 'information' && (
                <PaymentInformationModel
                  carData={data}
                  onSubmit={(downPayment: number) => {
                    setDownPaymentAmount(downPayment);
                    handleQuickOptionClicked(newLeadOption, downPayment);
                  }}
                  onBack={() => {
                    closeOnboardingForm();
                  }}
                  gotoDealershipWebsite={gotoDealershipWebsite}
                />
              )}

            {[
              'schedule_test_drive',
              'value_trade',
              'virtual_walkaround',
              'request_finance',
              'price_and_payments',
              'history',
              'live_agent',
            ].includes(newLeadOption.code) &&
              newLeadStep === 'onboarding' && (
                <OnboardingForm
                  onSubmit={handleSubmitOnboarding}
                  onBack={function (): void {
                    // newLeadCaptureOnClose();
                    closeOnboardingForm();
                  }}
                  newLeadOption={newLeadOption.code}
                  isLoading={isLoading}
                  title={newLeadOption.onboardingDialogDetails?.title}
                  description={
                    newLeadOption.onboardingDialogDetails?.description
                  }
                  submitButtonText={
                    newLeadOption.onboardingDialogDetails?.submitButtonText
                  }
                  footerText={newLeadOption.onboardingDialogDetails?.footerText}
                  imagePath={newLeadOption.onboardingDialogDetails?.imagePath}
                />
              )}

            {[
              'schedule_test_drive',
              'value_trade',
              'virtual_walkaround',
              'request_finance',
              'price_and_payments',
              'history',
              'live_agent',
            ].includes(newLeadOption.code) &&
              newLeadStep === 'otp' && (
                <OTPVerification
                  onVerify={handleVerifyOTP}
                  onBack={function (): void {
                    newLeadCaptureOnClose();
                  }}
                  isLoading={isLoading}
                  handleResend={handleResend}
                  timeLeft={timeLeft}
                  isResendDisabled={isResendDisabled}
                />
              )}

            {newLeadOption.code === 'schedule_test_drive' &&
              newLeadStep === 'confirmation' && (
                <MilesTimeSlotPicker
                  onSelectTimeSlot={async function (
                    _datetimeUTC: string,
                    _label: string,
                    rawDate: Date,
                  ): Promise<void> {
                    const payload = {
                      carId: data?.id ?? '',
                      actionType: LeadActionType.scheduleTestDrive,
                      scheduleDate: rawDate.toISOString(),
                    };

                    closeOnboardingForm();

                    const result = await api.requestLeadAction(payload);
                    if (result) {
                      toast.success(
                        'Information submitted successfully. A sales rep will be in touch shortly.',
                      );
                    }
                  }}
                  config={config}
                  api={api}
                />
              )}

            {newLeadOption.code === 'request_finance' &&
              newLeadStep === 'confirmation' && (
                <>
                  {/* <Box
                    sx={{
                      p: 3,
                      overflowY: 'auto',
                      flex: 1,
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant='h6'
                      fontWeight={600}
                      sx={{ mb: 2, textAlign: 'center' }}
                    >
                      {t('here_the_finance_app_that_your_requested')}
                    </Typography>
                    <Typography
                      variant='body2'
                      color='text.primary'
                      sx={{ mb: 3, textAlign: 'center' }}
                    >
                      {t('this_is_a_secure_link')}
                    </Typography>

                    <Box width={'100%'} textAlign={'center'}>
                      {financeResponse?.url ? (
                        <>
                          <a
                            href={financeResponse?.url}
                            target='_blank'
                            rel='noopener noreferrer'
                            style={{
                              textDecoration: 'none',
                              color: 'inherit',
                            }}
                          >
                            {shortenUrl(financeResponse?.url)}
                          </a>

                          <IconButton
                            onClick={() => {
                              handleCopyLink(financeResponse?.url);
                              closeOnboardingForm();
                            }}
                            sx={{ fontSize: '1.2rem' }}
                          >
                            <FaCopy />
                          </IconButton>
                        </>
                      ) : (
                        '-'
                      )}

                      {financeResponse?.url && (
                        <Button
                          variant='contained'
                          color='success'
                          fullWidth
                          sx={{ mt: 2, color: 'white' }}
                          onClick={() => {
                            window.open(financeResponse?.url, '_blank');
                            closeOnboardingForm();
                          }}
                        >
                          {t('button.go_to_finance_app')}
                        </Button>
                      )}
                    </Box>
                  </Box> */}
                  <PreOnboardingDetails
                    onContinue={() => handleQuickOptionClicked(newLeadOption)}
                    onBack={() => {
                      closeOnboardingForm();
                    }}
                    isLoading={isLeadActionLoading}
                    newLeadOption={newLeadOption.code}
                    title={newLeadOption.infoDialogDetails?.title}
                    description={newLeadOption.infoDialogDetails?.description}
                    featureTitle={newLeadOption.infoDialogDetails?.featureTitle}
                    featureDescription={
                      newLeadOption.infoDialogDetails?.featureDescription
                    }
                    features={newLeadOption.infoDialogDetails?.features ?? []}
                    submitButtonText={
                      newLeadOption.infoDialogDetails?.submitButtonText
                    }
                    footerText={newLeadOption.infoDialogDetails?.footerText}
                    imagePath={newLeadOption.infoDialogDetails?.imagePath}
                  />
                </>
              )}

            {newLeadOption.code === 'share_vehicle' && (
              <>
                <Box
                  sx={{
                    pb: 1,
                    overflowY: 'auto',
                    borderRadius: { xs: 2, sm: 3 },
                    flex: 1,
                    background: BACKGROUND_GRADIENT_TRANSPARENT,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 3,
                      py: 1,
                      mb: 2,
                      textAlign: 'center',
                      background:
                        'linear-gradient(to right, #B1CAFF, #ffffff, #ffffff, #ffffff)',
                      borderBottom: '1px solid',
                      borderColor: (theme) =>
                        alpha(theme.palette.primary.light, 0.1),
                    }}
                  >
                    <IconButton
                      onClick={newLeadCaptureOnClose}
                      sx={{ mr: 0.5, p: 0.5, pl: 0 }}
                      aria-label='Go back'
                    >
                      <KeyboardBackspace sx={{ color: 'primary.light' }} />
                    </IconButton>
                    <Typography fontWeight={400} color={'primary.light'}>
                      Back to Vehicle
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant='h5'
                      fontWeight={500}
                      sx={{ mb: 2, textAlign: 'center' }}
                    >
                      Share This Vehicle
                    </Typography>

                    <CardMedia
                      component='img'
                      image={
                        photoSource?.[0]
                          ? getAssetURL(
                            config.assetsUrl,
                            photoSource[0]?.key,
                            photoSource[0]?.url,
                          )
                          : `${config?.webUrl}/static/car_placeholder.png`
                      }
                      alt={data?.title}
                      sx={{
                        // height: { xs: 180, sm: 220 },
                        // width: 'auto',
                        width: '100%',
                        aspectRatio: '16 / 9',
                        objectFit: 'cover',
                        borderRadius: 1,
                        mb: 3,
                        boxShadow: 1,
                      }}
                    />

                    <Typography
                      variant='body1'
                      fontWeight={500}
                      sx={{ textAlign: 'center' }}
                    >
                      {data?.title}
                    </Typography>

                    <Divider
                      sx={{
                        my: 1,
                        width: '100%',
                        // borderColor: 'rgba(102, 126, 234, 0.2)'
                      }}
                    />

                    <Box width={'100%'} textAlign={'center'}>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ textAlign: 'center' }}
                      >
                        Copy link and paste it anywhere to share this vehicle.
                      </Typography>

                      {/* {data?.link ? (
                      <>
                        <a
                          href={data?.link}
                          target='_blank'
                          rel='noopener noreferrer'
                          style={{
                            textDecoration: 'none',
                            color: 'inherit',
                          }}
                        >
                          {shortenUrl(data?.link)}
                        </a>

                        <IconButton
                          onClick={() => {
                            handleCopyLink(data?.link);
                            closeOnboardingForm();
                          }}
                          sx={{ fontSize: '1.2rem' }}
                        >
                          <FaCopy />
                        </IconButton>
                      </>
                    ) : (
                      '-'
                    )} */}

                      {data?.link && (
                        <Button
                          variant='contained'
                          fullWidth
                          sx={{ mt: 2, background: BUTTON_BG_COLOR }}
                          startIcon={<ContentCopy />}
                          onClick={() => {
                            handleCopyLink(data?.link);
                            closeOnboardingForm();
                          }}
                        >
                          Copy Link
                        </Button>
                      )}
                    </Box>

                    {/* <Card
                      sx={{
                        mt: 2,
                        p: 1,
                        bgcolor: '#F1ECF7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                          alignItems: 'flex-start',
                          flex: 1,
                        }}
                      >
                        <BookmarkBorder color='primary' />
                        <Typography
                          sx={{ fontSize: typographyClamps.fontSize14 }}
                        >
                          {t('save_this_vehicle')}
                          <br />
                          <Typography
                            color='text.secondary'
                            sx={{ fontSize: typographyClamps.fontSize11 }}
                          >
                            {t('save_vehicle_login_prompt')}
                          </Typography>
                        </Typography>
                      </Box>

                      <Button
                        variant='outlined'
                        sx={{
                          m: 0,
                          fontSize: '10px',
                          px: 1,
                          borderRadius: '1px',
                        }}
                        onClick={gotoLoginPage}
                      >
                        {t('button.log_in_to_save')}
                      </Button>
                    </Card> */}
                  </Box>
                </Box>
              </>
            )}
          </DialogContent>
        </Dialog>
      )}

      {similarCarsToggle && (
        <ViewSimilarCarsDialog
          toggle={similarCarsToggle}
          onClose={similarCarsOnClose}
          gotoLoginPage={() => { }}
          onVehicleSelected={(selectedVehicle: any) => {
            if (selectedVehicle?.id !== data?.id) {
              setCarData(null);
              setCarId(selectedVehicle.id);
              // navigate(removeHost(selectedVehicle.link), {
              //   replace: true,
              // });
            }
            similarCarsOnClose();
          }}
          onGetTodaysPriceClicked={(selectedVehicle: any) => {
            if (selectedVehicle?.id !== data?.id) {
              setCarData(selectedVehicle);
              // navigate(removeHost(selectedVehicle.link), {
              //   replace: true,
              // });
            }

            handleQuickOptionClicked(
              actionButtons.find((item) => item.code === 'price_and_payments'),
            );
          }}
          carData={data}
          api={api}
          config={config}
        />
      )}

      {contactDealerToggle && (
        <DealershipContactDialog
          open={contactDealerToggle}
          onClose={contactDealerOnClose}
          handleOpenLocation={() => handleOpenLocation()}
          dealerSettings={dealerSettingsData}
          dealershipName={data?.dealerName}
        />
      )}
    </>
  );
};

export default CarDetailsDialog;
